import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { FOOTNOTE_AREA_STYLE, FOOTNOTE_RULE_STYLE, FOOTNOTE_ITEM_STYLE } from "../brand";

// Nota de rodape. O marcador (numero sobrescrito no texto) guarda o conteudo
// da nota; a numeracao e a area de notas no fim do texto sao derivadas dele e
// mantidas pelo plugin abaixo. Assim o HTML salvo ja sai completo — marcador e
// notas, com estilo em linha — para quem so exibe o HTML (portal, e-mail) e
// para o conversor do PDF.
const footnoteKey = new PluginKey("documentFootnotes");

export const Footnote = Node.create({
    name: "footnote",
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,

    addAttributes() {
        return {
            text: {
                default: "",
                parseHTML: (element) => element.getAttribute("data-footnote") || "",
                renderHTML: (attributes) => ({ "data-footnote": attributes.text, title: attributes.text }),
            },
            number: {
                default: 1,
                parseHTML: (element) => parseInt(element.textContent, 10) || 1,
                renderHTML: () => ({}),
            },
        };
    },

    parseHTML() {
        // Prioridade acima da marca de sobrescrito, que tambem le <sup>.
        return [{ tag: "sup[data-footnote]", priority: 60 }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return ["sup", mergeAttributes(HTMLAttributes), String(node.attrs.number)];
    },

    renderText({ node }) {
        return `[${node.attrs.number}]`;
    },

    addCommands() {
        const isFootnote = (selection) => !!selection.node && selection.node.type.name === this.name;
        return {
            // Com uma nota selecionada, troca o texto dela; senao insere uma nova
            // logo depois do trecho selecionado, como no Word.
            setFootnote: (text) => ({ state, chain }) => {
                const value = String(text || "").trim();
                if (!value) return false;
                if (isFootnote(state.selection)) return chain().updateAttributes(this.name, { text: value }).run();
                return chain().insertContentAt(state.selection.to, { type: this.name, attrs: { text: value } }).run();
            },
            unsetFootnote: () => ({ state, commands }) => (isFootnote(state.selection) ? commands.deleteSelection() : false),
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: footnoteKey,
                appendTransaction(transactions, oldState, newState) {
                    if (!transactions.some((transaction) => transaction.docChanged)) return null;
                    try {
                        return syncFootnotes(newState);
                    } catch (error) {
                        // Nota fora de sincronia e melhor que travar a edicao; a
                        // proxima alteracao tenta de novo.
                        return null;
                    }
                },
            }),
        ];
    },
});

// Area de notas no fim do texto. Nao e editavel: reflete os marcadores.
export const FootnoteList = Node.create({
    name: "footnoteList",
    group: "block",
    atom: true,
    selectable: false,

    addAttributes() {
        return {
            notes: {
                default: [],
                parseHTML: (element) => [...element.querySelectorAll("p")].map((item) => {
                    const clone = item.cloneNode(true);
                    const number = clone.querySelector("sup");
                    if (number) number.remove();
                    return clone.textContent.trim();
                }),
                renderHTML: () => ({}),
            },
        };
    },

    parseHTML() {
        return [{ tag: "div[data-footnotes]" }];
    },

    renderHTML({ node }) {
        return [
            "div",
            { "data-footnotes": "true", style: FOOTNOTE_AREA_STYLE },
            ["hr", { style: FOOTNOTE_RULE_STYLE }],
            ...node.attrs.notes.map((text, index) => ["p", { style: FOOTNOTE_ITEM_STYLE }, ["sup", {}, String(index + 1)], ` ${text}`]),
        ];
    },
});

function sameNotes(current, expected) {
    return current.length === expected.length && current.every((text, index) => text === expected[index]);
}

// Renumera os marcadores na ordem em que aparecem e deixa uma unica area de
// notas, como ultimo bloco do texto, com o conteudo deles. Devolve a transacao
// de ajuste ou null quando ja esta tudo no lugar.
export function syncFootnotes(state) {
    const { doc, schema, tr } = state;
    const marks = [];
    const areas = [];
    doc.descendants((node, pos) => {
        if (node.type.name === "footnote") marks.push({ node, pos });
        else if (node.type.name === "footnoteList") areas.push({ node, pos });
    });
    if (!marks.length && !areas.length) return null;

    let changed = false;
    // setNodeMarkup nao muda o tamanho do no: as posicoes seguem validas.
    marks.forEach(({ node, pos }, index) => {
        if (node.attrs.number === index + 1) return;
        tr.setNodeMarkup(pos, null, { ...node.attrs, number: index + 1 });
        changed = true;
    });

    const notes = marks.map(({ node }) => node.attrs.text);
    const inPlace = areas.length === 1 && doc.lastChild === areas[0].node && sameNotes(areas[0].node.attrs.notes, notes);
    if (notes.length ? !inPlace : areas.length) {
        // De tras para frente para nao deslocar as posicoes.
        [...areas].reverse().forEach(({ node, pos }) => tr.deleteRange(pos, pos + node.nodeSize));
        if (notes.length) tr.insert(tr.doc.content.size, schema.nodes.footnoteList.create({ notes }));
        changed = true;
    }

    return changed ? tr : null;
}
